// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/HomeExtras.tsx
// 🔧 PURPOSE: "Extra · Aggiungi al tuo soggiorno" della vista S (mockup
//             Main), dal catalogo vero (useExtrasPreview):
//             - in evidenza CIBO E BEVANDE ("Portato in casa"), con il prezzo
//               "da" della famiglia o, senza prezzo, la sua riga; durante il
//               soggiorno si mette in vetrina questo e non il Cestino della
//               Nonna (quello è della vista B, prima di arrivare);
//             - tre tessere: Scorte & comfort (il "da"), Pulizia extra
//               ("fascia 7–11") e Taxi & transfer;
//             - la fascia del late check-out quando l'offerta è già
//               "disponibile" e non è stata presa (la regola della vecchia
//               Home): "ULTIMO GIORNO" la vigilia e il giorno della partenza,
//               "PARTENZA" negli altri giorni. Ai gruppi il channel manager
//               non la vende (group_booking): qui non si mostra. Il giorno
//               della partenza sparisce ALL'ORARIO DI CHECK-OUT (l'orario
//               dell'offerta, 10:00), nello stesso istante in cui il channel
//               manager smette di venderla (checkout_passed): non un'ora
//               dopo, e nemmeno con un catalogo caricato prima delle 10:00;
//             - dall'orario di check-out più un'ora (stayOver, che tiene
//               conto del late check-out comprato) le famiglie che si
//               consegnano o si fanno IN CASA (cibo e bevande, scorte e
//               comfort, pulizia) non si mostrano più: l'ospite è già
//               uscito, e il channel manager non le vende più. Taxi e
//               transfer restano: per andare in aeroporto o in stazione
//               servono proprio adesso.
//
//             Il tocco porta dove portava la vecchia Home (groupTarget, con
//             il "torna indietro" alla Home). Col catalogo arrivato, una
//             famiglia che adesso non vende niente (il giorno della partenza,
//             per esempio) non si mostra; prima che arrivi si mostrano tutte,
//             con la loro riga al posto del prezzo. Senza niente da mostrare
//             la sezione non c'è.
// ============================================

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { HomeState } from '../../lib/homeState';
import { checkoutTimeReached } from '../../lib/romeDay';
import type { AppExtra, ExtrasCatalog } from '../../lib/guestApi';
import { EXTRA_GROUPS, groupStartingPrice, groupTarget, type ExtraGroup } from '../../lib/extraGroups';
import { formatEuro } from '../TouristTaxWidget';
import { CardShell, GoldButton, INK_TEXT, MoreLink, MUTED_TEXT, Pill, Section, SectionTitle } from './ui';

/** Da dove si arriva: il "torna indietro" degli extra riporta alla Home. */
const FROM_HOME = { from: '/' };

type GroupId = ExtraGroup['id'];

const groupById = (id: GroupId): ExtraGroup => EXTRA_GROUPS.filter((g) => g.id === id)[0];

/** Le tre tessere sotto la card in evidenza, in quest'ordine. */
const TILE_GROUPS: GroupId[] = ['supplies', 'clean', 'taxi'];

/**
 * Le famiglie consegnate o fatte dentro l'appartamento: dall'orario di
 * check-out più un'ora (stayOver) non si vendono più. Il taxi no: si va via.
 */
export const IN_HOUSE_GROUPS: GroupId[] = ['foodDrinks', 'supplies', 'clean'];

/** La famiglia si può ancora proporre in questo momento del soggiorno. */
export function groupAllowedNow(id: GroupId, state: Pick<HomeState, 'stayOver'>): boolean {
  return !(state.stayOver && IN_HOUSE_GROUPS.indexOf(id) >= 0);
}

/** Una famiglia vende qualcosa adesso (o si può chiedere). */
export function groupOnSale(group: Pick<ExtraGroup, 'serviceIds'>, items: AppExtra[]): boolean {
  return items.some((i) => group.serviceIds.indexOf(i.serviceId) >= 0 && (i.available || i.canRequest));
}

/**
 * La fascia del late check-out: offerta "disponibile", non ancora presa,
 * non per i gruppi e non dall'orario di check-out del giorno della partenza
 * in poi (l'orario dell'offerta, `fromTime`: lo stesso istante in cui il
 * channel manager smette di venderla). null = niente fascia.
 */
export function lateCheckoutBand(
  catalog: ExtrasCatalog | null,
  state: Pick<HomeState, 'isGroup' | 'stayOver' | 'checkOutDay' | 'checkoutTime'>,
  now: Date = new Date(),
): { toTime: string; price: number } | null {
  const offer = catalog && catalog.success ? catalog.lateCheckout : null;
  if (!offer || offer.status !== 'disponibile' || state.isGroup || state.stayOver) return null;
  // Il catalogo può essere di prima delle 10:00: l'orario si guarda qui.
  if (checkoutTimeReached(state.checkOutDay, offer.fromTime || state.checkoutTime, now)) return null;
  const bought = !!offer.purchasedAt || (catalog?.purchasedExtras || []).some((e) => e.serviceId === 'late_checkout');
  if (bought) return null;
  return { toTime: offer.toTime, price: offer.price };
}

/** Il "da € 1,50" di una famiglia fra i servizi in vendita, o null. */
const fromPrice = (group: ExtraGroup, items: AppExtra[] | null): number | null =>
  items ? groupStartingPrice(group, items.filter((i) => i.available)) : null;

const CARD_LINK = cn(
  'flex w-full rounded-[18px] text-left transition-colors hover:bg-nva-bone/60 dark:hover:bg-stone-800/50',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-nva-sea dark:focus-visible:ring-sky-400',
);

/** La card grande di Cibo e bevande: l'illustrazione a sinistra, il "da" in basso. */
const FeaturedFood: React.FC<{ price: number | null }> = ({ price }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'it';
  const group = groupById('foodDrinks');
  return (
    <CardShell className="overflow-hidden">
      <Link to={groupTarget(group)} state={FROM_HOME} className={cn(CARD_LINK, 'min-h-[150px]')}>
        <span aria-hidden="true" className="relative w-[132px] flex-shrink-0 bg-nva-cream min-[380px]:w-[148px] dark:bg-stone-800">
          <img src={group.image} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        </span>
        <span className="flex min-w-0 flex-grow flex-col gap-1 px-3.5 py-3">
          {/* "DOSTAWA DO MIESZKANIA" va su due righe a 360px: un po' d'aria fra le righe. */}
          <Pill className="leading-[1.2]">{t('home.extras.featuredBadge')}</Pill>
          <span className={cn('break-words font-display text-[19px] font-bold leading-tight', INK_TEXT)}>
            {t('extras.groups.foodDrinks.title')}
          </span>
          <span className={cn('text-[12.5px] leading-[1.35]', MUTED_TEXT)}>{t('home.extras.featuredDesc')}</span>
          <span className="mt-auto flex items-center justify-between gap-2 pt-1">
            <span className={cn('min-w-0 text-[14px] font-extrabold leading-snug', INK_TEXT, price === null && 'font-bold')}>
              {price !== null
                ? t('extras.store.from', { price: formatEuro(price, locale) })
                : t('extras.groups.foodDrinks.tagline')}
            </span>
            <span className={cn('flex flex-shrink-0 items-center gap-0.5 text-[13px] font-bold', INK_TEXT)}>
              {t('home.extras.see')}
              <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
          </span>
        </span>
      </Link>
    </CardShell>
  );
};

/** Una tessera piccola: l'illustrazione sopra, nome e riga sotto (da sola: in fila). */
const GroupTile: React.FC<{ group: ExtraGroup; sub: string; wide?: boolean }> = ({ group, sub, wide = false }) => {
  const { t } = useTranslation();
  return (
    <CardShell className="overflow-hidden">
      <Link
        to={groupTarget(group)}
        state={FROM_HOME}
        className={cn(CARD_LINK, 'h-full', wide ? 'min-h-[78px] flex-row items-stretch' : 'flex-col')}
      >
        <span aria-hidden="true" className={cn('relative flex-shrink-0 bg-nva-cream dark:bg-stone-800', wide ? 'w-[104px]' : 'h-[78px] w-full')}>
          <img src={group.image} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        </span>
        <span className={cn('flex min-w-0 flex-col gap-px', wide ? 'justify-center px-3.5 py-2.5' : 'px-[9px] pb-2.5 pt-2')}>
          {/* Nomi lunghi in una tessera stretta (tedesco, polacco): vanno a capo anche a metà parola. */}
          <span className={cn('text-[13.5px] font-bold leading-tight [overflow-wrap:anywhere]', INK_TEXT)}>
            {t(`extras.groups.${group.id}.title`)}
          </span>
          <span className={cn('text-[12px] leading-snug [overflow-wrap:anywhere]', MUTED_TEXT)}>{sub}</span>
        </span>
      </Link>
    </CardShell>
  );
};

/** L'anello del fuoco sul bottone oro dentro la fascia espresso (come la reception). */
const GOLD_ON_CARD_FOCUS =
  'focus-visible:ring-nva-bone dark:focus-visible:ring-nva-bone focus-visible:ring-offset-2 focus-visible:ring-offset-nva-espresso dark:focus-visible:ring-offset-stone-900';

/** La fascia espresso del late check-out, come nel mockup Main. */
const LateBand: React.FC<{ eyebrow: string; toTime: string; price: number }> = ({ eyebrow, toTime, price }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'it';
  return (
    <div className="flex items-center gap-3 rounded-[18px] bg-nva-espresso p-4 dark:border dark:border-stone-800 dark:bg-stone-900">
      <div className="flex min-w-0 flex-grow flex-col gap-[3px]">
        <span className="text-[10.5px] font-extrabold uppercase leading-snug tracking-eyebrow text-nva-gold">{eyebrow}</span>
        <h3 className="m-0 break-words font-display text-[19px] font-bold leading-tight text-nva-bone">
          {t('extras.lateCheckout.title')}
        </h3>
        <p className="m-0 text-[13px] leading-snug text-nva-foot">{t('home.extras.lateDesc', { time: toTime })}</p>
      </div>
      <div className="flex flex-shrink-0 flex-col items-end gap-1.5">
        {price > 0 ? (
          <span className="font-display text-[20px] font-bold leading-none tabular-nums text-nva-bone">{formatEuro(price, locale)}</span>
        ) : null}
        <GoldButton to="/extras?service=late_checkout" className={GOLD_ON_CARD_FOCUS}>
          {t('home.extras.lateCta')}
        </GoldButton>
      </div>
    </div>
  );
};

export interface HomeExtrasProps {
  state: HomeState;
  /** L'ora del clock della Home (useNow): la fascia del late check-out sparisce all'orario di uscita. */
  now: Date;
  /** Il catalogo degli extra; null finché non arriva (si mostrano le famiglie senza prezzi). */
  catalog: ExtrasCatalog | null;
  className?: string;
}

const HomeExtras: React.FC<HomeExtrasProps> = ({ state, now, catalog, className }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'it';
  const titleId = 'home-extras-title';

  // Col catalogo arrivato si filtra su quello che si vende adesso; senza,
  // si mostra tutto (il tocco porta comunque alla pagina giusta).
  const items = catalog && catalog.success ? catalog.items || [] : null;
  // Dopo l'uscita (stayOver) niente di quello che si porta in casa, qualunque
  // cosa dica un catalogo caricato prima.
  const shown = (id: GroupId) => groupAllowedNow(id, state) && (!items || groupOnSale(groupById(id), items));

  const showFood = shown('foodDrinks');
  const foodPrice = showFood ? fromPrice(groupById('foodDrinks'), items) : null;
  const tiles = TILE_GROUPS.filter(shown).map((id) => {
    const group = groupById(id);
    let sub: string;
    if (id === 'clean') sub = t('home.extras.cleanSub');
    else if (id === 'taxi') sub = t('home.extras.taxiSub');
    else {
      const from = fromPrice(group, items);
      sub = from !== null ? t('extras.store.from', { price: formatEuro(from, locale) }) : t(`extras.groups.${id}.tagline`);
    }
    return { group, sub };
  });
  const late = lateCheckoutBand(catalog, state, now);

  if (!showFood && tiles.length === 0 && !late) return null;

  return (
    <Section labelledBy={titleId} className={className}>
      <SectionTitle
        id={titleId}
        eyebrow={t('home.extras.eyebrow')}
        title={t('home.sections.buyable')}
        action={<MoreLink to="/extras">{t('home.extras.all')}</MoreLink>}
      />
      {showFood ? <FeaturedFood price={foodPrice} /> : null}
      {tiles.length > 0 ? (
        <div className={cn('grid gap-2', tiles.length === 3 ? 'grid-cols-3' : tiles.length === 2 ? 'grid-cols-2' : 'grid-cols-1')}>
          {tiles.map(({ group, sub }) => (
            <GroupTile key={group.id} group={group} sub={sub} wide={tiles.length === 1} />
          ))}
        </div>
      ) : null}
      {late ? (
        <LateBand
          eyebrow={state.isEve || state.isCheckoutDay ? t('home.extras.lateLast') : t('home.extras.lateEyebrow')}
          toTime={late.toTime}
          price={late.price}
        />
      ) : null}
    </Section>
  );
};

export default HomeExtras;
